import { Check, Circle, Loader2, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

// SimplyFunded brand blue
const SF_BLUE = "#3B70E0";

// Base steps for all applications
const baseSteps = [
  { id: 1, label: "Contract", key: "contract" },
  { id: 2, label: "Bank CoP", key: "bank_verified" },
  { id: 3, label: "Initiate Payment", key: "standing_order" },
  { id: 4, label: "LoanDisk", key: "loandisk" },
];

// Get steps based on application type
const getSteps = (isTopUp) => {
  if (isTopUp) {
    return [
      ...baseSteps,
      { id: 5, label: "Net in Hand", key: "net_in_hand" },
      { id: 6, label: "Pre-Fund Call", key: "prefunding" },
      { id: 7, label: "Disburse", key: "disbursement" },
    ];
  }
  return [
    ...baseSteps,
    { id: 5, label: "Pre-Fund Call", key: "prefunding" },
    { id: 6, label: "Disburse", key: "disbursement" },
  ];
};

export function WorkflowProgress({ application, compact = false, labels = null }) {
  const isTopUp = application?.is_topup || application?.loan_type === "TopUp";
  const steps = getSteps(isTopUp).map((s) => (labels?.[s.key] ? { ...s, label: labels[s.key] } : s));
  
  const getStepStatus = (stepKey) => {
    if (!application) return "pending";
    
    switch (stepKey) {
      case "contract":
        // Contract & DocuSign combined stage
        if (application.docusign_status === "signed" || application.docusign_status === "completed") return "completed";
        if (application.docusign_status === "sent" || application.docusign_status === "delivered" || application.docusign_status === "viewed") return "current";
        if (application.contract_generated) return "current";
        return "pending";
      case "bank_verified":
        return application.bank_details_status === "verified" ? "completed" : 
               application.bank_details_status === "pending_approval" ? "current" : "pending";
      case "standing_order":
        return application.standing_order_initiated ? "completed" : "pending";
      case "loandisk":
        return application.loandisk_validated ? "completed" : 
               application.loandisk_loan_id ? "current" : "pending";
      case "net_in_hand":
        return application.net_in_hand_confirmed ? "completed" :
               application.net_in_hand_approval_pending ? "current" : "pending";
      case "prefunding":
        return application.prefunding_call_completed ? "completed" : "pending";
      case "disbursement":
        return application.disbursement_confirmed ? "completed" : 
               (application.is_topup ? application.net_in_hand_confirmed : application.test_payment_confirmed) ? "current" : "pending";
      default:
        return "pending";
    }
  };

  const getCompletedCount = () => {
    return steps.filter(step => getStepStatus(step.key) === "completed").length;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1" data-testid="workflow-progress-compact">
        {steps.map((step, index) => {
          const status = getStepStatus(step.key);
          return (
            <div
              key={step.id}
              className={cn(
                "w-2 h-2 rounded-full",
                status === "completed" && "bg-primary",
                status === "current" && "bg-primary/50",
                status === "pending" && "bg-muted-foreground/30"
              )}
              title={step.label}
            />
          );
        })}
        <span className="ml-2 text-xs text-muted-foreground">
          {getCompletedCount()}/{steps.length}
        </span>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="workflow-progress">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(step.key);
          return (
            <div key={step.id} className="flex items-center flex-1">
              <div className="progress-step">
                <div
                  className={cn(
                    "progress-step-circle",
                    status === "completed" && "completed",
                    status === "current" && "current",
                    status === "pending" && "pending"
                  )}
                >
                  {status === "completed" ? (
                    <Check className="w-4 h-4" />
                  ) : status === "current" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : step.key === "prefunding" ? (
                    <Phone className="w-3 h-3" />
                  ) : (
                    <Circle className="w-3 h-3" />
                  )}
                </div>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium text-center",
                    status === "completed" && "text-primary",
                    status === "current" && "text-primary",
                    status === "pending" && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "progress-connector mt-[-20px]",
                    status === "completed" ? "completed" : "pending"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WorkflowProgressBar({ application }) {
  const isTopUp = application?.is_topup || application?.loan_type === "TopUp";
  
  const getProgress = () => {
    if (!application) return 0;
    let completed = 0;
    let total = isTopUp ? 7 : 6; // Different total based on deal type
    
    // Contract & DocuSign combined
    if (application.docusign_status === "signed" || application.docusign_status === "completed") completed++;
    // Bank CoP
    if (application.bank_details_status === "verified") completed++;
    // Standing Order
    if (application.standing_order_initiated) completed++;
    // LoanDisk
    if (application.loandisk_validated) completed++;
    // Net in Hand (TopUp only)
    if (isTopUp && application.net_in_hand_confirmed) completed++;
    // Pre-Funding Call
    if (application.prefunding_call_completed) completed++;
    // Disbursement
    if (application.disbursement_confirmed) completed++;
    
    return (completed / total) * 100;
  };

  return (
    <div className="w-full" data-testid="workflow-progress-bar">
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${getProgress()}%`, backgroundColor: SF_BLUE }}
        />
      </div>
    </div>
  );
}
